import { useRef, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GraduationCap, Video, ChevronDown, ChevronUp, Volume2, VolumeX, Play } from "lucide-react";

function IntroVideo() {
  const [muted, setMuted] = useState(true);
  const ref = useRef(null);
  const toggleSound = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };
  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-slate-950" data-testid="training-intro-video">
      <video ref={ref} autoPlay muted loop playsInline preload="auto" src="/dz-talking-video-v2.mp4"
        poster="/dz-talking-v2-frame.jpg" className="w-full max-h-[420px] object-cover" data-testid="training-intro-video-player" />
      <button onClick={toggleSound} data-testid="training-intro-sound-btn"
        className="absolute bottom-3 right-3 rounded-full bg-white/90 hover:bg-white text-slate-900 text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5 transition-colors duration-150">
        {muted
          ? <><VolumeX className="h-3.5 w-3.5" /> Tap for sound</>
          : <><Volume2 className="h-3.5 w-3.5" /> Sound on</>}
      </button>
    </div>
  );
}

function LessonVideo({ poster, title }) {
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const play = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = false;
    v.play().catch(() => {});
    setPlaying(true);
  };
  return (
    <div className="relative border-b border-slate-100 bg-slate-950">
      <video ref={ref} src="/dz-talking-video-v2.mp4" poster={poster} preload="none" controls={playing}
        className="w-full max-h-[380px] object-cover" data-testid="lesson-video-player" />
      {!playing && (
        <button onClick={play} data-testid="lesson-video-play-btn"
          className="absolute inset-0 flex items-center justify-center bg-slate-900/40 hover:bg-slate-900/30 transition-colors duration-150"
          aria-label={`Play video for ${title}`}>
          <span className="h-16 w-16 rounded-full bg-white/95 text-[#0B2545] flex items-center justify-center shadow-xl">
            <Play className="h-7 w-7 ml-1" />
          </span>
        </button>
      )}
    </div>
  );
}

const STAFF_LESSONS = [
  {
    img: "/training/t01_dashboard.jpeg",
    title: "Lesson 1 — Your Operations Dashboard",
    duration: "2 min",
    steps: [
      "Sign in and you land on the Operations Dashboard.",
      "The top row shows live KPIs: active clients, disputes in flight, overdue 30-day timers, resolved items, collected revenue, and estimated MRR.",
      "Below that, the Client Pipeline shows every client in one of four stages: Lead, Onboarded, Active, Resolved. Click any card to open that client's file.",
      "The 30-Day FCRA Timers panel lists every dispute waiting on a bureau, sorted by urgency. Green means comfortable, amber means under 10 days, red means overdue.",
      "Recent Activity is your audit trail — every login, view, mailing, and payment is recorded.",
    ],
    script: "Welcome to your operations dashboard. This is mission control. Across the top you have your six key numbers — how many clients are active, how many dispute letters are in the mail, and whether any bureau has blown its 30-day deadline. Below, the pipeline shows every client by stage. And on the right, the activity feed proves exactly who did what and when. Check this page first thing every morning — if anything is red, that's your first call of the day.",
  },
  {
    img: "/training/t02_clients.jpeg",
    title: "Lesson 2 — Adding & Managing Clients",
    duration: "2 min",
    steps: [
      "Open Clients from the sidebar. Search by name or email, or filter by pipeline status.",
      "Click 'New Client' to add someone manually — name, email, phone. Tick 'Create client portal login' and set a temporary password if they should get portal access.",
      "Clients who self-register from the public sign-up page appear here automatically as Leads.",
      "Every new client automatically gets a setup-fee invoice — you never have to remember to bill it.",
    ],
    script: "The Clients page is your CRM. Every client you'll ever work with lives here. Use the search box to find anyone instantly, or filter by stage. To add someone by hand — say they signed up over the phone — click New Client, fill in their details, and optionally give them a portal login on the spot. The moment a client is created, the system opens their setup-fee invoice for you, so nothing falls through the cracks.",
  },
  {
    img: "/training/t03_client_items.jpeg",
    title: "Lesson 3 — Report Review with AI Flagging",
    duration: "3 min",
    steps: [
      "Open a client, go to the Negative Items tab.",
      "Upload their credit report PDF — pick the bureau from the dropdown — and click Upload.",
      "Click 'Analyze with AI'. In about 30 seconds the system reads the report and flags every negative item: collections, charge-offs, late payments, inquiries.",
      "Review each flagged row. Click the green check to Confirm items worth disputing, or the X to Dismiss.",
      "Use 'Add manually' for anything the AI missed — pick a legal basis from the dispute-reason dropdown.",
    ],
    script: "This is where the heavy lifting happens. Upload the client's credit report PDF, choose the bureau, and hit Analyze with AI. The system reads the whole report and flags every derogatory item with a reason. Now your job is judgment: confirm the items that are genuinely disputable, dismiss the rest. Never dispute items you know are accurate — bureaus penalize frivolous disputes. If the AI missed something, add it manually and pick the legal basis, like re-aging or Metro 2 non-compliance.",
  },
  {
    img: "/training/t04_disputes.jpeg",
    title: "Lesson 4 — The Dispute Center",
    duration: "2 min",
    steps: [
      "Open 'Disputes & Mail' from the sidebar to see every letter across every client.",
      "Each row shows the client, letter type, bureau, status, the live 30-day clock, and the USPS certified tracking number.",
      "Filter by status — Drafted, Mailed, In Bureau Review, Responded, Resolved.",
      "Use 'Mail all drafted' to send every prepared letter in one batch — each gets its own certified tracking number.",
      "Physical certified mail is the official channel. Bureaus are never emailed.",
    ],
    script: "The dispute center is your mailroom. Every letter, every client, one table. Watch the 30-day clock column — that's the statutory deadline the bureau is under from the moment your letter lands. When you've drafted several letters, don't send them one by one — hit Mail all drafted and the whole batch goes out certified with tracking. Remember: certified physical mail is the only channel that creates the legal paper trail. We never email disputes to bureaus.",
  },
  {
    img: "/training/t05_dispute_detail.jpeg",
    title: "Lesson 5 — Sending a Letter & Logging the Outcome",
    duration: "3 min",
    steps: [
      "From a client's Disputes tab, click 'New Dispute Letter', pick the bureau and template, tick the confirmed items, and Generate.",
      "Review the full letter preview — client identity, masked SSN, and item details are auto-filled.",
      "Click 'Send certified mail', confirm the bureau address, and the letter goes out via Click2Mail with USPS tracking. The 30-day clock starts.",
      "When the bureau's response arrives, open the dispute, pick the outcome — Deleted, Updated, Verified, or Mixed — and click 'Mark resolved'.",
      "Deleted outcomes update the client's item list automatically. Verified items get your next-round letter.",
    ],
    script: "Let's send a dispute. From the client's Disputes tab, generate a letter — the system fills in everything: their name, address, masked Social, and every item with its legal basis. Read the preview once, then send it certified. The timeline on the right records every step forever — drafted, mailed, clock started. Thirty days later, when the bureau responds, come back here and log the outcome. If it's deleted, the item closes itself. If they verified it, that's your cue to escalate with a Round 2 method-of-verification letter.",
  },
  {
    img: "/training/t11_staff_billing.jpeg",
    title: "Lesson 6 — Billing, PayPal & CashApp",
    duration: "2 min",
    steps: [
      "Open Billing to see every invoice: collected total, outstanding total, and per-client history.",
      "Clients pay online with PayPal in their own portal — payments land here automatically, marked Paid.",
      "For CashApp: when a client tells you they paid, click 'Record CashApp' on their pending invoice, enter their $Cashtag or transaction reference, and save.",
      "Create ad-hoc invoices (like monthly service) from the client's Billing tab.",
    ],
    script: "Billing is deliberately simple. Clients pay themselves through their portal with PayPal — you do nothing. CashApp is the only manual part: when a client says they sent money, find their pending invoice, hit Record CashApp, and enter their cashtag or reference number so you can always reconcile later. The top cards show collected versus outstanding at a glance, so you always know who owes what.",
  },
];

const CLIENT_LESSONS = [
  {
    img: "/training/t06_portal_home.jpeg",
    title: "Client Lesson 1 — Your Progress Page",
    duration: "2 min",
    steps: [
      "After signing in, clients see their personal progress page.",
      "The score journey shows where they started, where they are, and their goal.",
      "The setup checklist walks them through four steps — intake, documents, agreement, payment — with a 'next step' card that links straight to whatever is left.",
      "The Items in Dispute section shows exactly what's being challenged, with the bureau and days remaining in plain language.",
    ],
    script: "When your client logs in, this is what they see — and it's designed to keep them calm and informed. Their score journey is front and center so progress feels real. The checklist tells them exactly what to do next, one step at a time. And below, every item you're disputing for them is listed in plain English — no jargon — with how many days the bureau has left to respond. Clients who understand the process stay patient, and this page does that work for you.",
  },
  {
    img: "/training/t07_portal_intake.jpeg",
    title: "Client Lesson 2 — Completing the Intake Form",
    duration: "2 min",
    steps: [
      "Clients open 'Intake Form' from the top menu.",
      "They enter phone, date of birth, and Social Security Number — the SSN is encrypted and only the last 4 digits are ever shown.",
      "Address details matter: bureaus verify identity against them on every dispute letter.",
      "Goals and current score help you personalize their plan.",
    ],
    script: "The intake form takes your client about two minutes. The most important part is accuracy — their name, date of birth, and address must match what the bureaus have, because every dispute letter gets verified against these details. Their Social Security number is encrypted the moment it's saved, and nobody ever sees more than the last four digits. They can come back and update this form any time.",
  },
  {
    img: "/training/t08_portal_documents.jpeg",
    title: "Client Lesson 3 — Uploading Documents",
    duration: "2 min",
    steps: [
      "Clients open 'My Documents' and pick what they're uploading: ID, proof of address, credit report, or SSN card.",
      "For credit reports, they also pick the bureau — Equifax, Experian, or TransUnion.",
      "Files land in their private encrypted vault, visible only to their case team.",
      "You need from every client: one government ID, one proof of address, and a credit report from each bureau.",
    ],
    script: "Documents are the fuel for disputes, and this page makes uploading painless. Your client picks the document type, chooses the file, and hits Upload securely. For credit reports they'll say which bureau it came from. Everything goes into an encrypted vault that only your case team can open — and every access is logged. What you need from every client: a photo ID, proof of address like a utility bill, and a fresh credit report from all three bureaus.",
  },
  {
    img: "/training/t09_portal_sign.jpeg",
    title: "Client Lesson 4 — Signing the Agreement",
    duration: "1 min",
    steps: [
      "Clients open 'Agreement', click 'Load agreement', and read the full service agreement including their CROA cancellation rights.",
      "They type their full legal name as their signature and tick the consent box.",
      "Click 'Sign agreement' — done. The signed copy is stored with a timestamp.",
    ],
    script: "Before any work begins, your client signs the service agreement electronically. They read it right here — it includes their legal right to cancel within three business days. To sign, they simply type their full legal name and tick the consent box. The signed agreement is stored with the exact date and time, protecting both you and the client.",
  },
  {
    img: "/training/t10_portal_billing.jpeg",
    title: "Client Lesson 5 — Paying an Invoice",
    duration: "1 min",
    steps: [
      "Clients open 'Billing' to see anything due and their full payment history.",
      "They click 'Pay with PayPal' on an open invoice — no card details ever touch your system.",
      "CashApp is supported too: they send payment to your $Cashtag and you apply it from the staff side.",
    ],
    script: "Paying is one tap. Your client opens Billing, sees what's due — the setup fee first — and pays with PayPal. Their card details never pass through our system. If they'd rather use CashApp, the instructions are right there: send to your cashtag, include their name, and your team applies it within one business day. Their full payment history is always visible, so there's never a dispute about what's been paid.",
  },
];

function LessonCard({ lesson, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`lesson-${index}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors duration-150 text-left">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[#0B2545] text-white flex items-center justify-center shrink-0">
            <Video className="h-4 w-4" />
          </div>
          <div>
            <div className="font-heading font-semibold text-slate-900">{lesson.title}</div>
            <div className="text-xs text-slate-400">{lesson.duration} watch-equivalent · {lesson.steps.length} steps</div>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="border-t border-slate-100">
          <LessonVideo poster={lesson.img} title={lesson.title} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="p-5">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">Step by step</div>
              <ol className="space-y-2.5">
                {lesson.steps.map((s, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-slate-700">
                    <span className="h-5 w-5 rounded-full bg-sky-100 text-sky-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
            <div className="p-5 bg-slate-50 border-t lg:border-t-0 lg:border-l border-slate-100">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">Narration script — read this while recording</div>
              <p className="text-sm text-slate-600 leading-relaxed italic">"{lesson.script}"</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TrainingCenter() {
  return (
    <div className="space-y-6" data-testid="training-center-page">
      <div className="flex items-center gap-3">
        <GraduationCap className="h-8 w-8 text-[#0B2545]" />
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Training Center</h1>
          <p className="text-sm text-slate-500">Watch the welcome video, then walk through every workflow below — each lesson plays a video walkthrough with step-by-step notes.</p>
        </div>
      </div>

      <IntroVideo />

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 leading-relaxed" data-testid="training-video-tip">
        <b>Want real videos?</b> Open a free screen recorder (Loom, Zoom, or your phone's screen record), play through a lesson below while reading its narration script out loud, and you'll have a professional training video in minutes. Each lesson's screenshot shows exactly what should be on screen.
      </div>

      <Tabs defaultValue="staff">
        <TabsList data-testid="training-tabs">
          <TabsTrigger value="staff" data-testid="training-tab-staff">Staff Training (6 lessons)</TabsTrigger>
          <TabsTrigger value="client" data-testid="training-tab-client">Client Onboarding (5 lessons)</TabsTrigger>
        </TabsList>
        <TabsContent value="staff" className="space-y-4 mt-4">
          {STAFF_LESSONS.map((l, i) => <LessonCard key={l.title} lesson={l} index={i} />)}
        </TabsContent>
        <TabsContent value="client" className="space-y-4 mt-4">
          <p className="text-sm text-slate-500">Use these to train new clients, or send them as your onboarding sequence. This is exactly what your clients see in their portal.</p>
          {CLIENT_LESSONS.map((l, i) => <LessonCard key={l.title} lesson={l} index={i} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
